/* eslint-disable @typescript-eslint/no-explicit-any */
import { Alert, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { getPurchaseTransaction } from '../../api/AppClip/transaction';
import { BlurLoader, ErrorComponent, ErrorModalComponent, SheetHeaderLabel } from '../../components/AppClip';
import Footer from '../../components/AppClip/OrderSuccessScreen/Footer';
import Flex from '../../components/Common/Flex';
import { AppConfig, DEFAULT_TIMEZONE, FETCH_ERROR, GA_EVENTS, GENERAL_ERRORS, SCREEN_IDS } from '../../constants';
import {
  RootState,
  customiseGiftActions,
  paymentActions,
  purchaseActions,
  recipientActions,
  scheduledDeliveryActions,
  screenActions,
  useAppDispatch,
  useAppSelector,
} from '../../store';
import { styles, useStyles } from '../../styles/AppClip/OrderSuccessScreenStyles';
import { CardPurchaseTransaction, ILabels, SEND_DATE_OPTIONS } from '../../types';
import { isAppleEnvironment } from '../../utils/helpers';
import { useAppImages } from '../../hooks/useAppImages';
import { useIsLargerScreen } from '../../hooks';
import { GaEventsContext } from '../../contexts/GaEventsContext';
import { useParams } from 'react-router-dom';

const { selectAmountScreen, orderSuccessScreen } = SCREEN_IDS;
const { setScreen } = screenActions;
const { resetRecipient } = recipientActions;
const { setSelectedAmount: setSelectedAmountRedux, resetUUID4 } = purchaseActions;
const { resetPayment } = paymentActions;
const { resetAnimation } = customiseGiftActions;
const { reset: resetScheduledDelivery } = scheduledDeliveryActions;

type OrderInfoProps = {
  purchaseTransaction?: CardPurchaseTransaction;
  isLoading?: boolean;
  totalAmount: number;
};

const SolidBarIcon = () => {
  const classes = useStyles();
  return <Flex className={classes.solidBarIcon} />;
};

const DividerBarIcon = ({ customStyle = {} }) => {
  return <Flex style={styles.dividerBarIcon(customStyle)} />;
};

const OrderInfo = ({ purchaseTransaction, isLoading, totalAmount }: OrderInfoProps) => {
  const [divHeight, setDivHeight] = useState(0);
  const { scheduledDelivery } = useAppSelector((state: RootState) => state.schedule);

  const { orderId, paymentDetails } = purchaseTransaction || {};
  const recipient = purchaseTransaction?.recipient;
  const orderNumberDisplay = orderId;

  const deliveryDate = moment.tz(scheduledDelivery.deliveryDate, DEFAULT_TIMEZONE).format('DD MMM YYYY');
  const deliveryTime = moment.tz(scheduledDelivery.deliveryDate, DEFAULT_TIMEZONE).format('hh:mm A');
  const deliveryStatusDisplay = scheduledDelivery.deliveryOptionDate === SEND_DATE_OPTIONS.NOW ? 'Sent' : deliveryDate;

  const setDivRef = (el: HTMLDivElement) => {
    if (!el) return;
    setDivHeight(el.getBoundingClientRect().height);
  };

  return (
    <>
      <div ref={setDivRef}>
        <Labels label="Order #" value={orderNumberDisplay} testId={'order-success-screen-orderid'} />
        <Labels
          label="Recipient's full name"
          value={`${recipient?.firstName} ${recipient?.lastName}`}
          testId={'order-success-screen-recipient-name'}
        />
        <Labels
          label="Payment details"
          value={`**** ${paymentDetails?.card?.last4 || ''}`}
          testId={'order-success-screen-payment-details'}
        />
        <Labels
          containerStyle={{ marginBottom: 0 }}
          label="Total paid"
          value={`AUD $${totalAmount.toFixed(2)}`}
          testId={'order-success-screen-total-paid'}
        />
        <div style={{ fontSize: '13px', color: 'var(--text-darkOnLight-60, rgba(0, 0, 0, 0.60))' }}>
          Incl. purchase fee & GST
        </div>
        <Labels
          containerStyle={{ marginTop: 26 }}
          label="Delivery Status"
          value={deliveryStatusDisplay}
          testId={'order-success-screen-delivery-status'}
        />
        {scheduledDelivery.deliveryOptionDate === SEND_DATE_OPTIONS.SCHEDULED && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              color: 'var(--text-darkOnLight-60, rgba(0, 0, 0, 0.60))',
            }}
          >
            <div style={{ fontSize: '13px' }} data-testid={'success-screen-schedule-delivery-timezone'}>
              (AEST time zone)
            </div>
            <div
              style={{
                marginLeft: 'auto',
                fontSize: '13px',
                color: 'var(--text-offBlackOnLight-100, #172136)',
              }}
              data-testid={'success-screen-schedule-delivery-time'}
            >
              {deliveryTime}
            </div>
          </div>
        )}
      </div>

      {isLoading ? <BlurLoader height={divHeight} /> : null}
    </>
  );
};

const Labels: FC<ILabels> = ({ label, value, containerStyle, testId }) => {
  const theme = useTheme();

  return (
    <Flex style={containerStyle || styles.labelContainer}>
      <Flex style={styles.labelTextContainer}>
        <p style={styles.subLabelStyle(theme)} data-testid={`${testId ? testId + '-label' : ''}`}>
          {label}
        </p>
      </Flex>
      <Flex style={styles.labelValueContainer}>
        <p data-testid={`${testId ? testId + '-value' : ''}`} style={styles.subLabelValueStyle(theme)}>
          {value}
        </p>
      </Flex>
    </Flex>
  );
};

const OrderSuccessScreen: FC = () => {
  console.log('rerender OrderSuccessScreen');

  const dispatch = useAppDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useIsLargerScreen();

  const params = useParams();
  const merchantName: string | undefined = params?.merchantName || AppConfig.DEFAULT_MERCHANT_NAME;
  const { card } = useAppImages(merchantName || '');
  const { recipientDetails } = useAppSelector((state: RootState) => state.recipient);
  const { scheduledDelivery } = useAppSelector((state: RootState) => state.schedule);
  const { totalAmount, appClipTransactionId } = useAppSelector((state: RootState) => state.purchase);
  const { sender } = useAppSelector((state: RootState) => state.payment);
  const { firstName, mobileNumberDisplay } = recipientDetails;

  const [purchaseTransaction, setPurchaseTransaction] = useState<CardPurchaseTransaction>();
  const [isLoading, setIsLoading] = useState(true);
  const isLoadingRef = useRef<boolean>(isLoading);
  const [error, setError] = useState<any>(null);
  const { conversion } = useContext(GaEventsContext);
  useMemo(() => {
    if (!appClipTransactionId) {
      dispatch(setScreen({ screen: selectAmountScreen, loading: false, updating: false }));
      return;
    }
  }, []);

  const {
    id: errorID = '',
    message: errorMessage = '',
    label: errorLabel = '',
    buttonLabel: errorBtnLabel = '',
  } = error || {};
  const { ErrorOnSubmit, ErrorOnPageLoad, ErrorSystemOnSubmit, ErrorNotFound, ErrorCardCreationFailed } =
    GENERAL_ERRORS;

  const isApple = isAppleEnvironment();

  const recipientContact = useMemo(() => {
    return mobileNumberDisplay || scheduledDelivery?.mobileNumberDisplay || scheduledDelivery?.emailAddress;
  }, [mobileNumberDisplay, scheduledDelivery]);

  //Clean User details session
  const resetStates = () => {
    batch(() => {
      dispatch(resetRecipient());
      dispatch(setSelectedAmountRedux(50));
      dispatch(resetPayment());
      dispatch(resetAnimation());
      dispatch(resetUUID4());
      dispatch(resetScheduledDelivery());
    });
  };

  const onClickContinue = () => {
    resetStates();
    dispatch(
      setScreen({
        screen: selectAmountScreen,
        loading: false,
        updating: false,
        isCreditCard: 0,
        isExpansion: false,
      }),
    );
  };

  const handleCompleted = (data?: CardPurchaseTransaction) => {
    if (conversion) {
      conversion(
        GA_EVENTS.SUCCESS || '',
        {
          value: totalAmount.toFixed(2),
          transaction_id: data?.orderId,
        },
        'purchase',
      );
    }
    setIsLoading(false);
    isLoadingRef.current = false;
    dispatch(resetPayment());
  };

  const handleError = (err?: any) => {
    if (err?.message === FETCH_ERROR) {
      setError(ErrorOnPageLoad);
      setIsLoading(false);
      isLoadingRef.current = false;
    } else {
      setError(ErrorSystemOnSubmit);
      setIsLoading(true);
      isLoadingRef.current = true;
    }
    dispatch(resetPayment());
  };

  const startQueryPurchaseTransactionDetails = async () => {
    // redirect to first page if appClipTransactionId is not set
    if (!appClipTransactionId) {
      dispatch(setScreen({ screen: selectAmountScreen, loading: false, updating: false }));
      return;
    }

    try {
      // handle status
      const response = await getPurchaseTransaction(appClipTransactionId, AppConfig.APP_ID);

      const data = response?.data;
      if (data && response?.status === 'success') {
        setPurchaseTransaction(data);
        handleCompleted(data);
      } else if (response?.status === 'not_found') {
        setError(ErrorNotFound);
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    } catch (error) {
      handleError(error);
    }
  };

  const onPressHandler = () => {
    setError(null);
    switch (errorID) {
      case ErrorOnSubmit.id:
        return console.log('Press me ErrorOnSubmit');
      case ErrorOnPageLoad.id:
        return dispatch(setScreen({ screen: orderSuccessScreen, loading: false, updating: false }));
      case ErrorSystemOnSubmit.id:
        return dispatch(setScreen({ screen: selectAmountScreen, loading: false, updating: false }));
      case ErrorNotFound.id:
        return dispatch(setScreen({ screen: selectAmountScreen, loading: false, updating: false }));
      default:
        return;
    }
  };

  const onWindowClose = (_: BeforeUnloadEvent) => {
    resetStates();
  };

  // change message depend on purchase status
  const purchaseMessage = useMemo(() => {
    const { orderCreated, metadata, orderId } = purchaseTransaction || {};
    const { recipient, delivery } = metadata || {};
    const contact = delivery?.toLowerCase() === 'sms' ? recipient?.mobile : recipient?.email;

    switch (orderCreated) {
      case 'success':
        return `Your Gift Card has been sent to ${firstName || recipient?.firstName} on ${
          recipientContact || contact
        }.`;
      case 'failure':
        return (
          <Alert severity="error" icon={false} className={classes.alert}>
            {ErrorCardCreationFailed.message}
          </Alert>
        );
      default:
        return `We are sending your Gift Card to ${firstName || recipient?.firstName} on ${
          recipientContact || contact
        }.`;
    }
  }, [purchaseTransaction, firstName, recipientContact, ErrorCardCreationFailed, classes]);
  useEffect(() => {
    startQueryPurchaseTransactionDetails();
    window.addEventListener('beforeunload', onWindowClose, true);
    return () => {
      window.removeEventListener('beforeunload', onWindowClose, true);
    };
  }, []);

  return errorID === ErrorOnPageLoad.id ? (
    <Flex className={classes.errorContainer}>
      <ErrorComponent label={errorLabel} message={errorMessage} buttonLabel={errorBtnLabel} onPress={onPressHandler} />
    </Flex>
  ) : (
    <Flex className={classes.mainContainer}>
      {isDesktop && (
        <Flex justifyContent="center">
          <Box
            component="img"
            src={card}
            data-testid={'success-screen-card-image'}
            sx={{ maxWidth: 325, borderRadius: 2, mb: 3.625 }}
          />
        </Flex>
      )}
      <ErrorModalComponent
        open={[ErrorOnPageLoad.id, ErrorSystemOnSubmit.id, ErrorNotFound.id].includes(errorID)}
        label={errorLabel}
        message={errorMessage}
        buttonLabel={errorBtnLabel}
        onPress={onPressHandler}
      />
      <SolidBarIcon />
      <SheetHeaderLabel
        label={`Thanks ${sender?.firstName}!`}
        subLabel={purchaseMessage}
        labelStyle={{ textAlign: 'center' }}
      />
      <DividerBarIcon />
      <p style={styles.label(theme)} data-testid={'success-screen-order-details-text'}>
        Order details
      </p>
      <OrderInfo purchaseTransaction={purchaseTransaction} isLoading={isLoading} totalAmount={totalAmount} />
      <DividerBarIcon customStyle={{ marginTop: 24 }} />
      <Footer onClickContinue={onClickContinue} isApple={isApple} isLoading={isLoading} />
    </Flex>
  );
};

export default OrderSuccessScreen;
